import './Mock.scss';
import { Alert } from '@lmig/lmds-react-alert';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { Button } from '@lmig/lmds-react-button';
import { Toggle } from '@lmig/lmds-react-toggle';
import { useUserData } from 'Foundation/User/UserContext';
import { useEffect, useRef, useState } from 'react';
import { CanMockUser, GetAgentsByAgency, IMockerUserResponse, MockUser } from '../Helpers/AdminApi';
import { IAgencyContactsModel } from './MockModels';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { Link } from '@lmig/lmds-react-link';

export function Mock() {
  const inputRef = useRef<HTMLInputElement>(null);
  const autoSearch = useRef(false);

  const { userData } = useUserData();
  const [mocking, setMocking] = useState(userData?.isMockingAgent ?? false);
  const [agentId, setAgentId] = useState(userData?.isMockingAgent ? userData?.dpecid : '');
  const [mockError, setMockError] = useState('');

  const [searchedValue, setSearchedValue] = useState('');
  const [agents, setAgents] = useState<IAgencyContactsModel[]>([]);
  const [canMockAgent, setCanMockAgent] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [searchStatCodeError, setSearchStatCodeError] = useState<'error' | undefined>();

  useEffect(() => {
    setMockError('');

    // Show suggestions if users types in the same search term again
    if (agentId !== '' && agentId === searchedValue) setShowSuggestions(true);
    else setShowSuggestions(false);

    if (autoSearch.current) {
      // Force a search even if looks like stat code
      submitMock();
    }
  }, [agentId]);

  useEffect(() => {
    // Focus input on mock toggle
    if (mocking) inputRef.current?.focus();
  }, [mocking]);

  function isStatCode(value: string) {
    return /^\d{6,8}$/.test(value);
  }

  async function submitMock() {
    if (autoSearch.current || !isStatCode(agentId)) {
      // Don't trigger mock if empty string
      if (agentId !== '') {
        let responseMock: IMockerUserResponse | null = null;
        responseMock = await MockUser(agentId.toLowerCase().trim(), mocking);
        autoSearch.current = false;
        handleMockResponse(responseMock);
      }
    } else {
      statCodeSearch();
    }
  }

  function clearSuggestions() {
    setMockError('');
    setShowSuggestions(false);
    setAgents([]);
    setSearchStatCodeError(undefined);
    setSearchedValue('');
    setCanMockAgent(true);
  }

  async function mockToggle() {
    clearSuggestions();
    if (mocking) {
      // Don't unmock if user isn't already mocking. Reduce page refresh
      if (userData?.isMockingAgent) {
        // If currently mocking and turning off mock, clear mock and update server
        const responseMock = await MockUser('', false);
        handleMockResponse(responseMock);
      }
      setMocking(!mocking);
    } else {
      setMocking(!mocking);
    }
  }

  function handleMockResponse(responseMock: IMockerUserResponse | null) {
    if (responseMock?.error === null) {
      setMockError('Error Mocking?');
    } else if (responseMock?.error && responseMock.error !== '') {
      setMockError(responseMock.error);
    } else {
      ClearSessionStorageKeys();
      window.location.reload();
    }
  }

  async function statCodeSearch() {
    setMockError('');
    clearSuggestions();
    setAgentsLoading(true);
    let agents = await GetAgentsByAgency(agentId);
    setSearchedValue(agentId);
    if (agents.length !== 0) {
      setAgents(
        agents.sort((a, b) => {
          const nameA = a.lastName.toUpperCase();
          const nameB = b.lastName.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        }),
      );
    } else {
      setSearchStatCodeError('error');
    }
    let responseCanMock = await CanMockUser(agentId);
    if (responseCanMock !== null && responseCanMock.error !== null && responseCanMock.error === '') {
      setCanMockAgent(responseCanMock.found);
    }
    setAgentsLoading(false);
    setShowSuggestions(true);
  }

  function onSuggestionClick(suggestAgentId: string) {
    setMockError('');
    clearSuggestions();
    autoSearch.current = true;
    if (suggestAgentId !== agentId) {
      // useeffect triggers auto search on agentid change
      setAgentId(suggestAgentId);
    } else {
      submitMock();
    }
  }

  function ClearSessionStorageKeys() {
    // Clear session to adhere to personalization, but leave slider settings for publishers to mock easier
    const key = 'slider_settings';
    const sliderSettings = sessionStorage.getItem(key);
    sessionStorage.clear();
    if (sliderSettings) sessionStorage.setItem(key, sliderSettings);
    localStorage.removeItem(`favorites-${agentId}`);
  }

  return (
    <>
      {userData && !userData?.isAgent() && (
        <div className="mock-wrapper">
          <div className="container">
            <div>
              <div className="mock-toggle-col" style={{ padding: '5px' }}>
                <div>
                  <Toggle
                    compact
                    dynamicWidth
                    labelVisual="Agent mocking"
                    onChange={mockToggle}
                    defaultChecked={mocking}
                  />
                </div>
              </div>
              {mocking && (
                <>
                  <div  style={{ padding: '5px' }}>
                    Search for an agent below. Agent mocking only works in this portal.
                  </div>
                  <div className="mock-input-col" style={{ padding: '5px', marginBottom: '10px' }}>
                    <div className="search-input-suggestion-wrapper">
                      <div className="search-input-wrapper">
                          <AlphanumericInput
                            disabled={!mocking}
                            id="Alphanumeric--agentid"
                            aria-label="Agent Id"
                            labelVisual="User ID or agency code"
                            className="agentid-mock"
                            value={agentId}
                            innerRef={inputRef}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                submitMock();
                              }
                            }}
                            onFocus={() => {
                              if (agentId !== '' && agentId === searchedValue) setShowSuggestions(true);
                            }}
                            onBlur={() => {
                              setShowSuggestions(false);
                            }}
                            onChange={(e) => setAgentId(e.target.value)}
                            highlightType={searchStatCodeError}
                          />
                      </div>
                      {agentsLoading && (
                        <CircularProgressSpinner
                          className="portal-users-loading-spinner disabling-spinner"
                          size="16"
                          aria-label="Getting agency code users"
                        />
                      )}
                      {showSuggestions && (
                        <div className="search-suggest" role="listbox">
                          <div className="search-suggest-content">
                            <ul className="search-suggest-list">
                              {canMockAgent ? (
                                <li
                                  key={'agent' + agentId}
                                  onMouseDown={() => {
                                    onSuggestionClick(agentId);
                                  }}
                                  className="search-suggest-list-item"
                                  aria-label={agentId}
                                >
                                  <b>Mock user {agentId}</b>
                                </li>
                              ) : (
                                <li
                                  key={'agency' + searchedValue}
                                  className="search-suggest-list-item no-click"
                                  aria-label={searchedValue}
                                >
                                  <b>No user with ID {searchedValue}</b>
                                </li>
                              )}
                              {agents.length > 0 ? (
                                <>
                                  <li
                                    key={'agency' + searchedValue}
                                    style={{ paddingBottom: '10px' }}
                                    className="search-suggest-list-item no-click"
                                    aria-label={searchedValue}
                                  >
                                    <b>Users for agency code {searchedValue}</b>
                                  </li>
                                  {agents.map((agent) => {
                                    if (agent.userID && agent.userID !== '') {
                                      return (
                                        <li
                                          key={agent.userID}
                                          onMouseDown={() => {
                                            onSuggestionClick(agent.userID);
                                          }}
                                          className="search-suggest-list-item"
                                          style={{ paddingLeft: '25px', minHeight: '12px' }}
                                          aria-label={agent.userID}
                                        >
                                          {agent.lastName},&nbsp;
                                          {agent.firstName}&nbsp;({agent.roles.replaceAll(',', ', ')})&nbsp;
                                          {agent.userID}
                                        </li>
                                      );
                                    }
                                  })}
                                </>
                              ) : (
                                <li
                                  key={'noagency'}
                                  className="search-suggest-list-item no-click"
                                  aria-label={searchedValue}
                                >
                                  <b>{searchedValue} is not a valid agency code</b>
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className="align-self-center mock-input-button-col"
                    style={{ padding: '5px', marginBottom: '10px' }}
                  >
                    <div>
                      <Button size="medium" variant="primary" onClick={submitMock} disabled={!mocking} dynamicWidth={true}>
                        Search
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="row">
              <div>
                {userData.isMockingAgent ? (
                  <Alert highlightType="caution">
                    You are mocking {userData.name} (
                    {userData.selectedStatCodeData?.Role && userData.selectedStatCodeData?.Role !== ''
                      ? userData.selectedStatCodeData.Role
                      : ''}
                    ) {userData.dpecid}
                  </Alert>
                ) : (
                  <Alert highlightType="informative">You are not mocking anyone.</Alert>
                )}
              </div>
            </div>
            {mockError !== '' && (
              <div className="row">
                <div>
                  <Alert highlightType="negative">{mockError}</Alert>
                </div>
              </div>
            )}
            <div className="row slider-show" style={{ display: 'none' }}>
              <div>
                <Link variant="standalone" caretPosition="none" target="_self" role="link" href="/admin/admintools">
                  View user profile data
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
